// 組織與場域 › 場域詳細的「設備」與「場域設定」分頁（SPEC #org-tree-ui 第 2 點「場域詳細三個分頁」；VMS UX 評審 2026-10-13 P1-7、B-25）。
//   設備：這個節點以下的設備（結構上的家戶 ∩ 我看得到的設備；看不到影像的戶只列戶名與台數）＋〔在影像牆開啟〕（切範圍到這個節點並填滿牆）。
//   場域設定：名稱與層級、時區、地址與座標、斷網可用（media_mode / offline_max_hours，PATCH …/nodes/{node}，SPEC #vms-web-offline）、
//            這個場域的家戶登記過本地 VMS 的電腦（管理員可撤銷）；每一區各自儲存。最下方「維護」區由 StructurePage 傳入。
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useScope } from '../../lib/scope';
import { orgApi, type MyOrg } from '../../lib/planeApi';
import { subtreeIds } from '../../lib/siteTree';
import { treeApi, type OrgNode } from '../../lib/orgTreeApi';
import { localVmsApi } from '../../lib/localVms';
import { otErr } from './otErr';
import { dateStamp } from '../../lib/timefmt';
import { modelDisplay, modelNameOf } from '../../lib/devices';

/** 斷網可用的時數上限（team 組織，SPEC #vms-web-offline 本地通行權杖 exp）。 */
export const OFFLINE_MAX_HOURS = 72;

export function SiteDevices({ org, nodeId, isRoot, name, pathText }: { org: MyOrg; nodeId: string; isRoot: boolean; name: string; pathText: string }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { devices, setScope } = useScope();
  const sites = useQuery({ queryKey: ['org-sites', org.org_id], queryFn: () => orgApi.sites(org), staleTime: 60_000 });
  const homes = useQuery({ queryKey: ['org-homes', org.org_id], queryFn: () => orgApi.homes(org), staleTime: 60_000 });
  const inTree = useMemo(() => {
    if (!sites.data || !homes.data) return [];
    const ids = subtreeIds(sites.data, nodeId);
    return homes.data.filter((h) => isRoot || ids.has(h.site_id));
  }, [sites.data, homes.data, nodeId, isRoot]);
  const homeIds = new Set(inTree.map((h) => h.home_id));
  const mine = devices.filter((d) => homeIds.has(d.home_id) && !d.container_only);
  const seen = new Set(mine.map((d) => d.home_id));
  const hidden = inTree.filter((h) => !seen.has(h.home_id));
  const homeName = new Map(inTree.map((h) => [h.home_id, h.display_name]));
  const openWall = () => {
    setScope(isRoot ? 'org:' + org.org_id : `node:${org.org_id}:${nodeId}`, isRoot ? org.display_name : pathText, { announce: t('scope.switched_to', { name: isRoot ? org.display_name : name }) });
    nav('/live', { state: { fillScope: true } });
  };
  if (sites.isLoading || homes.isLoading) return <p className="muted">{t('common.loading')}</p>;
  return (
    <div data-testid="site-devices">
      <div className="toolbar wrap">
        <span className="muted small">{t('ot.site_dev_count', { n: mine.length, homes: inTree.length })}</span>
        <span className="spacer" />
        {mine.length > 0 && (
          <button className="btn btn-sm btn-primary" onClick={openWall}>
            {t('ot.open_in_wall')}
          </button>
        )}
      </div>
      {mine.length > 0 && (
        <div className="table-wrap">
          <table className="grid ot-cards">
            <thead>
              <tr>
                <th>{t('ot.site_dev_name')}</th>
                <th>{t('ot.site_dev_model')}</th>
                <th>{t('ot.site_dev_status')}</th>
                <th>{t('ot.site_dev_home')}</th>
              </tr>
            </thead>
            <tbody>
              {mine.map((d) => (
                <tr key={d.home_id + d.device_ref}>
                  <td data-label={t('ot.site_dev_name')}>
                    <Link to={`/devices/${encodeURIComponent(d.home_id)}/${encodeURIComponent(d.device_ref)}`}>{d.display_name || modelDisplay(t, { model: d.model ?? '', ...modelNameOf(d as unknown as Record<string, unknown>) })}</Link>
                    {(d.children?.length ?? 0) > 0 && <span className="muted small"> {t('ot.site_dev_channels', { count: d.children!.length })}</span>}
                  </td>
                  {/* 機種用設備清單同一個函式（model_name），型號代碼放滑過的提示（第十二輪 B12-08） */}
                  <td data-label={t('ot.site_dev_model')} className="small" title={d.model || undefined} data-testid="site-dev-model">
                    {modelDisplay(t, { model: d.model ?? '', ...modelNameOf(d as unknown as Record<string, unknown>) })}
                  </td>
                  <td data-label={t('ot.site_dev_status')}>
                    <span className={'badge ' + (d.online ? 'badge-ok' : 'badge-warn')}>{d.online ? t('ot.site_dev_online') : t('ot.site_dev_offline')}</span>
                  </td>
                  <td data-label={t('ot.site_dev_home')} className="small muted">
                    {homeName.get(d.home_id)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {mine.length === 0 && hidden.length === 0 && <p className="muted">{t('ot.site_dev_empty')}</p>}
      {hidden.length > 0 && (
        <p className="muted small" data-testid="site-dev-hidden">
          {t('ot.site_dev_hidden', { n: hidden.length, names: hidden.slice(0, 5).map((h) => h.display_name).join('、') + (hidden.length > 5 ? '…' : '') })}
        </p>
      )}
    </div>
  );
}

export function SiteSettings({
  org,
  node,
  isRoot,
  canEdit,
  onSaved,
  maintenance,
}: {
  org: MyOrg;
  node: OrgNode | undefined;
  isRoot: boolean;
  canEdit: boolean;
  onSaved(msg: string): void;
  maintenance: ReactNode;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  // 改了會自動儲存（離開欄位就存）——畫面上寫出來，存好後顯示「✓ 已儲存」（第三輪 B3-19）
  const [saved, setSaved] = useState<string | null>(null);
  useEffect(() => {
    if (!saved) return;
    const id = window.setTimeout(() => setSaved(null), 4000);
    return () => window.clearTimeout(id);
  }, [saved]);
  if (!node) return <p className="muted">{t('common.loading')}</p>;
  const save = async (body: Omit<Parameters<typeof treeApi.updateNode>[2], 'expected_version'>, msg: string) => {
    await treeApi.updateNode(org, node.node_id, { expected_version: node.version, ...body });
    void qc.invalidateQueries({ queryKey: ['ot', org.org_id, 'node', node.node_id] });
    setSaved(msg);
    onSaved(msg);
  };
  return (
    <div className="form" data-testid="site-settings">
      {canEdit && (
        <p className="small muted" data-testid="site-autosave">
          {t('ot.set_autosave')}
          {saved && (
            <span className="ok-text" data-testid="site-saved">
              {' '}
              ✓ {saved}
            </span>
          )}
        </p>
      )}
      {!isRoot && (
        <div className="maint-row">
          <div>
            <strong>{t('ot.set_name')}</strong>
            <div className="muted small">{node.name}</div>
          </div>
          {/* 〔改名 / 層級〕只留頁首那一顆（第四輪 R4-P2-5） */}
        </div>
      )}
      <TzRow key={'tz' + node.version} node={node} canEdit={canEdit} save={save} />
      <GeoRow key={'geo' + node.version} node={node} canEdit={canEdit} save={save} />
      <OfflineRow key={'off' + node.version} org={org} node={node} canEdit={canEdit} save={save} />
      {maintenance}
    </div>
  );
}

type Save = (body: Omit<Parameters<typeof treeApi.updateNode>[2], 'expected_version'>, msg: string) => Promise<void>;

function useSaver(save: Save) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const run = async (body: Parameters<Save>[0], msg: string) => {
    setBusy(true);
    setErr(null);
    try {
      await save(body, msg);
    } catch (e) {
      setErr(otErr(t, e));
    } finally {
      setBusy(false);
    }
  };
  return { busy, err, run };
}

function TzRow({ node, canEdit, save }: { node: OrgNode; canEdit: boolean; save: Save }) {
  const { t } = useTranslation();
  const [tz, setTz] = useState(node.timezone ?? '');
  const s = useSaver(save);
  const zones = useMemo(() => {
    try {
      return (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? [];
    } catch {
      return [];
    }
  }, []);
  return (
    <fieldset className="field" data-testid="site-tz">
      <legend>{t('ot.set_tz')}</legend>
      <div className="row">
        <input
          className="input"
          list="site-tz-list"
          value={tz}
          disabled={!canEdit}
          onChange={(e) => setTz(e.target.value)}
          onBlur={() => tz.trim() !== (node.timezone ?? '') && void s.run({ timezone: tz.trim() }, t('ot.set_saved'))}
          placeholder={t('ot.set_tz_inherit')}
          aria-label={t('ot.set_tz')}
          style={{ maxWidth: 260 }}
        />
        <datalist id="site-tz-list">
          {zones.map((z) => (
            <option key={z} value={z} />
          ))}
        </datalist>
        {s.busy && <span className="muted small">{t('common.saving')}</span>}
      </div>
      <span className="muted small">{t('ot.set_tz_hint')}</span>
      {s.err && <p className="error small">{s.err}</p>}
    </fieldset>
  );
}

function GeoRow({ node, canEdit, save }: { node: OrgNode; canEdit: boolean; save: Save }) {
  const { t } = useTranslation();
  const [addr, setAddr] = useState(node.geo?.address ?? '');
  const [lat, setLat] = useState(node.geo?.lat !== undefined ? String(node.geo.lat) : '');
  const [lng, setLng] = useState(node.geo?.lng !== undefined ? String(node.geo.lng) : '');
  const s = useSaver(save);
  const num = (v: string) => (v.trim() === '' ? undefined : Number(v));
  const bad = [lat, lng].some((v) => v.trim() !== '' && !Number.isFinite(Number(v))) || (lat.trim() === '') !== (lng.trim() === '');
  const changed = addr.trim() !== (node.geo?.address ?? '') || num(lat) !== node.geo?.lat || num(lng) !== node.geo?.lng;
  // 離開欄位就存（UX-06 每列各自儲存；第二輪 R2-P1-9：不再每區一顆〔儲存〕）；格式不對不送
  const commit = () => {
    if (!canEdit || bad || !changed) return;
    void s.run({ geo: { ...(node.geo ?? {}), address: addr.trim() || undefined, lat: num(lat), lng: num(lng) } }, t('ot.set_saved'));
  };
  return (
    <fieldset className="field" data-testid="site-geo">
      <legend>{t('ot.set_geo')}</legend>
      <label className="field">
        <span>{t('ot.set_address')}</span>
        <input className="input" value={addr} disabled={!canEdit} onChange={(e) => setAddr(e.target.value)} onBlur={commit} />
      </label>
      <div className="row">
        <input className="input" style={{ width: 140 }} inputMode="decimal" value={lat} disabled={!canEdit} onChange={(e) => setLat(e.target.value)} onBlur={commit} aria-label={t('ot.set_lat')} placeholder={t('ot.set_lat')} />
        <input className="input" style={{ width: 140 }} inputMode="decimal" value={lng} disabled={!canEdit} onChange={(e) => setLng(e.target.value)} onBlur={commit} aria-label={t('ot.set_lng')} placeholder={t('ot.set_lng')} />
        {s.busy && <span className="muted small">{t('common.saving')}</span>}
      </div>
      {bad && <span className="error small">{t('ot.set_geo_bad')}</span>}
      {s.err && <p className="error small">{s.err}</p>}
    </fieldset>
  );
}

/** 斷網可用（B-25：本地 VMS 頁說「請組織管理員到場域設定開啟」的那個開關）＋這個場域的家戶登記過的電腦。 */
function OfflineRow({ org, node, canEdit, save }: { org: MyOrg; node: OrgNode; canEdit: boolean; save: Save }) {
  const { t } = useTranslation();
  const [on, setOn] = useState(node.media_mode === 'offline_allowed');
  const [hours, setHours] = useState(node.offline_max_hours ?? OFFLINE_MAX_HOURS);
  const s = useSaver(save);
  const team = org.kind === 'team';
  // 開關一改就存、時數離開欄位就存（UX-06；第二輪 R2-P1-9）
  const write = (nextOn: boolean, h: number) =>
    void s.run(nextOn ? { media_mode: 'offline_allowed', offline_max_hours: h } : { media_mode: 'cloud_required' }, nextOn ? t('ot.set_offline_saved_on') : t('ot.set_offline_saved_off'));
  return (
    <fieldset className="field" data-testid="site-offline">
      <legend>{t('ot.set_offline')}</legend>
      <label className="row-inline">
        <input
          type="checkbox"
          checked={on}
          disabled={!canEdit || s.busy}
          onChange={(e) => {
            setOn(e.target.checked);
            write(e.target.checked, hours);
          }}
        />
        {t('ot.set_offline_on')}
      </label>
      {on && (
        <label className="row-inline">
          {t('ot.set_offline_hours')}
          <input
            className="input"
            type="number"
            min={1}
            max={team ? OFFLINE_MAX_HOURS : undefined}
            style={{ width: 90 }}
            value={hours}
            disabled={!canEdit}
            onChange={(e) => setHours(Math.max(1, Math.min(team ? OFFLINE_MAX_HOURS : 720, Number(e.target.value) || 1)))}
            onBlur={() => hours !== (node.offline_max_hours ?? OFFLINE_MAX_HOURS) && write(true, hours)}
            aria-label={t('ot.set_offline_hours')}
          />
        </label>
      )}
      <span className="muted small">{t('ot.set_offline_hint')}</span>
      {s.err && <p className="error small">{s.err}</p>}
      <SiteComputers org={org} nodeId={node.node_id} />
    </fieldset>
  );
}

/** 這個節點底下（直屬）的家戶登記過本地 VMS 的電腦；管理員可撤銷（SPEC #vms-web-offline「撤銷」）。讀不到的家戶（沒有家戶管理權）略過。 */
function SiteComputers({ org, nodeId }: { org: MyOrg; nodeId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const kids = useQuery({ queryKey: ['ot', org.org_id, 'nodes', nodeId], queryFn: () => treeApi.children(org, nodeId) });
  const homes = (kids.data?.items ?? []).filter((x) => x.kind === 'home' && x.home_id);
  const lists = useQueries({
    queries: homes.map((h) => ({ queryKey: ['site-lvms', h.home_id], queryFn: () => localVmsApi.homeEnrollments(org.api, h.home_id!), retry: false })),
  });
  const rows = lists.flatMap((q, i) => (q.data ?? []).filter((e) => !e.ro).map((e) => ({ e, home: homes[i] })));
  const [err, setErr] = useState<string | null>(null);
  if (!rows.length) return null;
  return (
    <div data-testid="site-computers">
      <h4 className="small" style={{ margin: '8px 0 4px' }}>
        {t('ot.set_computers')}
      </h4>
      <ul className="plain-list small">
        {rows.map(({ e, home }) => (
          <li key={e.enr_id} className="row">
            <span>{e.label}</span>
            <span className="muted">
              {home.name} · {t('ot.set_computer_updated', { date: dateStamp(e.updated_at) })}
            </span>
            <span className="spacer" />
            <button
              className="btn btn-sm"
              onClick={() =>
                void localVmsApi
                  .homeRevoke(org.api, home.home_id!, e.enr_id)
                  .then(() => qc.invalidateQueries({ queryKey: ['site-lvms', home.home_id] }))
                  .catch((x) => setErr(otErr(t, x)))
              }
            >
              {t('localvms.revoke')}
            </button>
          </li>
        ))}
      </ul>
      {err && <p className="error small">{err}</p>}
    </div>
  );
}
